import { api } from './authService';

// Base API configuration
const LEAD_GEN_BASE = '/lead_gen';

// Campaign Management API
export const campaignsAPI = {
  // List campaigns
  getCampaigns: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/campaigns/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch campaigns: ${error.message}`);
    }
  },

  // Create campaign
  createCampaign: async (campaignData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/campaigns/`, campaignData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create campaign: ${error.message}`);
    }
  },

  // Get campaign details
  getCampaign: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/campaigns/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch campaign: ${error.message}`);
    }
  },

  // Update campaign
  updateCampaign: async (id, campaignData) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/campaigns/${id}/`, campaignData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update campaign: ${error.message}`);
    }
  },

  // Delete campaign
  deleteCampaign: async (id) => {
    try {
      await api.delete(`${LEAD_GEN_BASE}/campaigns/${id}/`);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete campaign: ${error.message}`);
    }
  },

  // Generate leads for campaign
  generateLeads: async (id, data) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/campaigns/${id}/generate/`, data);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to generate leads: ${error.message}`);
    }
  },

  // Pause campaign
  pauseCampaign: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/campaigns/${id}/pause/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to pause campaign: ${error.message}`);
    }
  },

  // Resume campaign
  resumeCampaign: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/campaigns/${id}/resume/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to resume campaign: ${error.message}`);
    }
  },

  // Cancel campaign
  cancelCampaign: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/campaigns/${id}/cancel/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to cancel campaign: ${error.message}`);
    }
  },
};

// Job Management API
export const jobsAPI = {
  // List generation jobs
  getJobs: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/jobs/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch jobs: ${error.message}`);
    }
  },

  // Get active jobs
  getActiveJobs: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/jobs/active/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch active jobs: ${error.message}`);
    }
  },

  // Get job details
  getJob: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/jobs/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch job: ${error.message}`);
    }
  },

  // Get job progress
  getJobProgress: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/jobs/${id}/progress/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch job progress: ${error.message}`);
    }
  },

  // Cancel job
  cancelJob: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/jobs/${id}/cancel/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to cancel job: ${error.message}`);
    }
  },

  // Retry failed job
  retryJob: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/jobs/${id}/retry/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to retry job: ${error.message}`);
    }
  },
};

// Results Management API
export const resultsAPI = {
  // List lead results
  getResults: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/results/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch results: ${error.message}`);
    }
  },

  // Get results by job
  getResultsByJob: async (jobId) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/results/by-job/${jobId}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch results by job: ${error.message}`);
    }
  },

  // Get results by campaign
  getResultsByCampaign: async (campaignId) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/results/by-campaign/${campaignId}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch results by campaign: ${error.message}`);
    }
  },

  // Get result details
  getResult: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/results/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch result: ${error.message}`);
    }
  },

  // Approve result
  approveResult: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/results/${id}/approve/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to approve result: ${error.message}`);
    }
  },

  // Reject result
  rejectResult: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/results/${id}/reject/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to reject result: ${error.message}`);
    }
  },

  // Import to CRM
  importResult: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/results/${id}/import/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to import result: ${error.message}`);
    }
  },

  // Bulk approve results
  bulkApproveResults: async (resultIds) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/results/bulk-approve/`, { result_ids: resultIds });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to bulk approve results: ${error.message}`);
    }
  },

  // Bulk reject results
  bulkRejectResults: async (resultIds) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/results/bulk-reject/`, { result_ids: resultIds });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to bulk reject results: ${error.message}`);
    }
  },

  // Bulk import to CRM
  bulkImportResults: async (resultIds) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/results/bulk-import/`, { result_ids: resultIds });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to bulk import results: ${error.message}`);
    }
  },

  // Export results
  exportResults: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/results/export/`, { 
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to export results: ${error.message}`);
    }
  },
};

// Lead Sources API
export const sourcesAPI = {
  // List lead sources
  getSources: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/sources/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch sources: ${error.message}`);
    }
  },

  // Create lead source
  createSource: async (sourceData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/sources/`, sourceData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create source: ${error.message}`);
    }
  },

  // Get source details
  getSource: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/sources/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source: ${error.message}`);
    }
  },

  // Update source
  updateSource: async (id, sourceData) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/sources/${id}/`, sourceData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update source: ${error.message}`);
    }
  },

  // Delete source
  deleteSource: async (id) => {
    try {
      await api.delete(`${LEAD_GEN_BASE}/sources/${id}/`);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete source: ${error.message}`);
    }
  },

  // Get source analytics
  getSourceAnalytics: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/sources/${id}/analytics/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source analytics: ${error.message}`);
    }
  },

  // Get source metrics
  getSourceMetrics: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/sources/${id}/metrics/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source metrics: ${error.message}`);
    }
  },

  // Get source history
  getSourceHistory: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/sources/${id}/history/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source history: ${error.message}`);
    }
  },
};

// Source Configuration API
export const sourceConfigsAPI = {
  // List source configurations
  getConfigs: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/source-configs/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source configs: ${error.message}`);
    }
  },

  // Create source config
  createConfig: async (configData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/source-configs/`, configData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create source config: ${error.message}`);
    }
  },

  // Get config details
  getConfig: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/source-configs/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source config: ${error.message}`);
    }
  },

  // Update config
  updateConfig: async (id, configData) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/source-configs/${id}/`, configData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update source config: ${error.message}`);
    }
  },

  // Delete config
  deleteConfig: async (id) => {
    try {
      await api.delete(`${LEAD_GEN_BASE}/source-configs/${id}/`);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete source config: ${error.message}`);
    }
  },

  // Test connection
  testConnection: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/source-configs/${id}/test/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to test connection: ${error.message}`);
    }
  },
};

// Suppression Lists API
export const suppressionListsAPI = {
  // List suppression lists
  getLists: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/selection/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch suppression lists: ${error.message}`);
    }
  },

  // Create suppression list
  createList: async (listData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/suppression-lists/`, listData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create suppression list: ${error.message}`);
    }
  },

  // Get list details
  getList: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch suppression list: ${error.message}`);
    }
  },

  // Update list
  updateList: async (id, listData) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/suppression-lists/${id}/`, listData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update suppression list: ${error.message}`);
    }
  },

  // Delete list
  deleteList: async (id) => {
    try {
      await api.delete(`${LEAD_GEN_BASE}/suppression-lists/${id}/`);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete suppression list: ${error.message}`);
    }
  },

  // Duplicate list
  duplicateList: async (id) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/suppression-lists/${id}/duplicate/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to duplicate suppression list: ${error.message}`);
    }
  },

  // Get list analytics
  getListAnalytics: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${id}/analytics/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch list analytics: ${error.message}`);
    }
  },

  // Get suppression logs
  getSuppressionLogs: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${id}/logs/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch suppression logs: ${error.message}`);
    }
  },

  // Get suppression logs with filters
  getSuppressionLogsWithFilters: async (filters = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-logs/`, { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch suppression logs: ${error.message}`);
    }
  },

  // Get list statistics
  getListStats: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${id}/stats/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch list statistics: ${error.message}`);
    }
  },

  // Export rules to CSV
  exportRules: async (id) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${id}/export/`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to export rules: ${error.message}`);
    }
  },

  // Add campaign association to suppression list
  addCampaignAssociationToSuppressionList: async (suppressionListId, campaignId) => {
    try {
      const response = await api.post(`/lead_gen/suppression-lists/campaign-association/`, {
        suppression_list_id: suppressionListId,
        campaign_id: campaignId
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to add campaign association: ${error.message}`);
    }
  },

  // Remove campaign association from suppression list
  removeCampaignAssociationFromSuppressionList: async (suppressionListId, campaignId) => {
    try {
      const response = await api.delete(`/lead_gen/suppression-lists/campaign-association/`, {
        data: {
          suppression_list_id: suppressionListId,
          campaign_id: campaignId
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to remove campaign association: ${error.message}`);
    }
  },
};

// Suppression Rules API
export const suppressionRulesAPI = {
  // List rules in list
  getRules: async (listId) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${listId}/rules/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch rules: ${error.message}`);
    }
  },

  // Create rule
  createRule: async (listId, ruleData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/suppression-lists/${listId}/rules/`, ruleData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create rule: ${error.message}`);
    }
  },

  // Get rule details
  getRule: async (listId, ruleId) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/${listId}/rules/${ruleId}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch rule: ${error.message}`);
    }
  },

  // Update rule
  updateRule: async (listId, ruleId, ruleData) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/suppression-lists/${listId}/rules/${ruleId}/`, ruleData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update rule: ${error.message}`);
    }
  },

  // Delete rule
  deleteRule: async (listId, ruleId) => {
    try {
      await api.delete(`${LEAD_GEN_BASE}/suppression-lists/${listId}/rules/${ruleId}/`);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete rule: ${error.message}`);
    }
  },
};

// Settings API
export const settingsAPI = {
  // Get account settings
  getAccountSettings: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/settings/account/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch account settings: ${error.message}`);
    }
  },

  // Update account settings
  updateAccountSettings: async (settings) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/settings/account/`, settings);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update account settings: ${error.message}`);
    }
  },

  // Get compliance rules
  getComplianceRules: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/settings/compliance/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch compliance rules: ${error.message}`);
    }
  },

  // Update compliance rules
  updateComplianceRules: async (rules) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/settings/compliance/`, rules);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update compliance rules: ${error.message}`);
    }
  },

  // Get integration settings
  getIntegrationSettings: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/settings/integrations/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch integration settings: ${error.message}`);
    }
  },

  // Update integration settings
  updateIntegrationSettings: async (settings) => {
    try {
      const response = await api.put(`${LEAD_GEN_BASE}/settings/integrations/`, settings);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update integration settings: ${error.message}`);
    }
  },

  // Test integrations
  testIntegrations: async () => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/settings/integrations/test/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to test integrations: ${error.message}`);
    }
  },
};

// Accounts API
export const accountsAPI = {
  // Get all accounts for the current user
  getAccounts: async () => {
    try {
      const response = await api.get('/crm/accounts/');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch accounts: ${error.message}`);
    }
  },

  // Get account details
  getAccount: async (accountId) => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch account: ${error.message}`);
    }
  },
};

// Utility API
export const utilityAPI = {
  // Get available CRM campaigns
  getCrmCampaigns: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/lookup/crm-campaigns/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch CRM campaigns: ${error.message}`);
    }
  },

  // Get lead type choices
  getLeadTypes: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/lookup/lead-types/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch lead types: ${error.message}`);
    }
  },

  // Get source type choices
  getSourceTypes: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/lookup/source-types/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch source types: ${error.message}`);
    }
  },

  // Get status choices
  getStatusChoices: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/lookup/status-choices/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch status choices: ${error.message}`);
    }
  },

  // Validate campaign data
  validateCampaign: async (campaignData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/validate/campaign/`, campaignData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to validate campaign: ${error.message}`);
    }
  },

  // Validate source data
  validateSource: async (sourceData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/validate/source/`, sourceData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to validate source: ${error.message}`);
    }
  },

  // Validate suppression data
  validateSuppression: async (suppressionData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/validate/suppression/`, suppressionData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to validate suppression: ${error.message}`);
    }
  },
};

// Real-time Validation API
export const realtimeAPI = {
  // Full lead data check
  checkLead: async (leadData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/realtime/check/`, leadData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to check lead: ${error.message}`);
    }
  },

  // Partial data check
  checkPartialLead: async (leadData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/realtime/check-partial/`, leadData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to check partial lead: ${error.message}`);
    }
  },

  // Single field check
  checkField: async (fieldData) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/realtime/check-field/`, fieldData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to check field: ${error.message}`);
    }
  },
};

// Bulk Import API
export const bulkImportAPI = {
  // Get available targets for import
  getAvailableTargets: async (importType, accountId) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/helper/available-targets/`, {
        params: { import_type: importType, account_id: accountId }
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch available targets: ${error.message}`);
    }
  },

  // Get suppression lists for selection
  getSuppressionListsForSelection: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/suppression-lists/selection/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch suppression lists for selection: ${error.message}`);
    }
  },

  // Get available campaigns for import
  getAvailableCampaigns: async (accountId) => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/campaigns/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch available campaigns: ${error.message}`);
    }
  },

  // Get campaigns for account
  getAccountCampaigns: async (accountId) => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/campaigns/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch account campaigns: ${error.message}`);
    }
  },

  // Upload file
  uploadFile: async (file, importType, accountId, options = {}) => {
    try {
      // Convert file to base64
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove the data URL prefix (e.g., "data:application/vnd.ms-excel;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Prepare JSON payload
      const payload = {
        file_name: file.name,
        file_content: fileBase64,
        file_type: file.type,
        file_size: file.size,
        import_type: importType,
        account_id: accountId
      };

      // Add optional fields if they exist
      if (options.targetSuppressionListId) {
        payload.target_suppression_list_id = options.targetSuppressionListId;
      }
      if (options.targetLeadSourceId) {
        payload.target_lead_source_id = options.targetLeadSourceId;
      }
      if (options.targetCampaignIds && options.targetCampaignIds.length > 0) {
        payload.target_campaign_ids = options.targetCampaignIds;
      }
      if (options.autoAssignCampaigns !== undefined) {
        payload.auto_assign_campaigns = options.autoAssignCampaigns;
      }
      if (options.campaignSpecificImport !== undefined) {
        payload.campaign_specific_import = options.campaignSpecificImport;
      }
      if (options.description) {
        payload.description = options.description;
      }
      if (options.skipDuplicates !== undefined) {
        payload.skip_duplicates = options.skipDuplicates;
      }
      if (options.validateData !== undefined) {
        payload.validate_data = options.validateData;
      }
      if (options.batchSize) {
        payload.batch_size = options.batchSize;
      }
      if (options.list_type) {
        payload.list_type = options.list_type;
      }

      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/upload/`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  },

  // Preview file
  previewFile: async (file, importType, previewRows = 10) => {
    try {
      // Convert file to base64
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove the data URL prefix (e.g., "data:application/vnd.ms-excel;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Prepare JSON payload
      const payload = {
        file_name: file.name,
        file_content: fileBase64,
        file_type: file.type,
        file_size: file.size,
        import_type: importType,
        preview_rows: previewRows
      };

      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/preview/`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to preview file: ${error.message}`);
    }
  },

  // List import jobs
  getImportJobs: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/jobs/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch import jobs: ${error.message}`);
    }
  },

  // Get job details
  getImportJob: async (jobId) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/jobs/${jobId}/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch import job: ${error.message}`);
    }
  },

  // Start job processing
  startJobProcessing: async (jobId) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/jobs/${jobId}/start_processing/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to start job processing: ${error.message}`);
    }
  },

  // Cancel job
  cancelJob: async (jobId) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/jobs/${jobId}/cancel/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to cancel job: ${error.message}`);
    }
  },

  // Retry failed job
  retryJob: async (jobId) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/jobs/${jobId}/retry/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to retry job: ${error.message}`);
    }
  },

  // Assign campaigns to existing import job
  assignCampaignsToJob: async (jobId, campaignIds) => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/jobs/${jobId}/assign-campaigns/`, {
        campaign_ids: campaignIds
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to assign campaigns to job: ${error.message}`);
    }
  },

  // Get job summary
  getJobSummary: async () => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/jobs/summary/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch job summary: ${error.message}`);
    }
  },

  // List import records
  getImportRecords: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/records/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch import records: ${error.message}`);
    }
  },

  // Get records with errors
  getRecordsWithErrors: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/records/errors/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch records with errors: ${error.message}`);
    }
  },

  // List import templates
  getImportTemplates: async (params = {}) => {
    try {
      const response = await api.get(`${LEAD_GEN_BASE}/bulk-import/templates/`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch import templates: ${error.message}`);
    }
  },

  // Create default templates
  createDefaultTemplates: async () => {
    try {
      const response = await api.post(`${LEAD_GEN_BASE}/bulk-import/templates/create_defaults/`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create default templates: ${error.message}`);
    }
  },
};

export default {
  campaigns: campaignsAPI,
  jobs: jobsAPI,
  results: resultsAPI,
  sources: sourcesAPI,
  sourceConfigs: sourceConfigsAPI,
  suppressionLists: suppressionListsAPI,
  suppressionRules: suppressionRulesAPI,
  settings: settingsAPI,
  accounts: accountsAPI,
  utility: utilityAPI,
  realtime: realtimeAPI,
  bulkImport: bulkImportAPI,
}; 